import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { BookDemoModal } from "./BookDemoModal";

const legal = [
  { name: "Privacy", href: "/privacy" },
  { name: "Terms", href: "/terms" },
  { name: "Security", href: "/security" },
];

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.9 2H22l-7.6 8.7L23 22h-6.8l-5.3-7-6.1 7H1.7l8.1-9.3L1 2h7l4.8 6.4L18.9 2Zm-2.4 18h1.9L7.6 4H5.6l10.9 16Z" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
  </svg>
);

const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
  else window.location.href = `/#${id}`;
};

const LINK_COLOR = "rgba(255,255,255,.74)";
const MUTED = "#9DB3A6";

export function Footer() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <>
      <footer
        style={{ backgroundColor: "#0D2B22", borderTop: "1px solid #1E4838" }}
        aria-labelledby="footer-heading"
      >
        <h2 id="footer-heading" className="sr-only">Footer</h2>

        {/* Main body */}
        <div className="w-full px-[clamp(20px,5vw,64px)] pt-[54px] pb-[30px]">

          {/* Top row: brand left | nav columns right */}
          <div
            className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-10 flex-wrap"
            style={{ paddingBottom: 28, borderBottom: "1px solid #1E4838" }}
          >
            {/* Brand */}
            <div className="flex-none">
              <Link to="/" aria-label="Revisr home" className="flex items-center gap-[11px]" style={{ textDecoration: "none" }}>
                <img src="/revisrlogo3.png" alt="" className="flex-none" style={{ width: 125, height: "auto", objectFit: "contain" }} />
              </Link>
              <p className="mt-3.5" style={{ fontSize: 13.5, color: MUTED, maxWidth: "30ch", lineHeight: 1.5 }}>
                Practice infrastructure for compliance consultants.
              </p>
            </div>

            {/* Nav columns */}
            <nav className="flex gap-[50px] flex-wrap">
              <div>
                <h3 style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 16, fontWeight: 600 }}>
                  Navigation
                </h3>
                <ul className="space-y-[11px] list-none p-0 m-0">
                  {[
                    { label: "For Consultants", action: () => scrollTo("problem") },
                    { label: "How It Works",    action: () => scrollTo("how") },
                    { label: "Contact",          action: () => setDemoOpen(true) },
                  ].map(({ label, action }) => (
                    <li key={label}>
                      <button
                        type="button"
                        onClick={action}
                        style={{ fontSize: 14, color: LINK_COLOR, background: "none", border: "none", cursor: "pointer", padding: 0, display: "block" }}
                        onMouseEnter={e => (e.currentTarget.style.color = "#2EB39A")}
                        onMouseLeave={e => (e.currentTarget.style.color = LINK_COLOR)}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 16, fontWeight: 600 }}>
                  Follow us
                </h3>
                <div className="flex items-center gap-5">
                  {[
                    { href: "https://www.linkedin.com/company/rivisr/", label: "LinkedIn", Icon: () => <Linkedin style={{ width: 16, height: 16 }} /> },
                    { href: "https://x.com/revisr_ai", label: "X", Icon: XIcon },
                    { href: "https://www.facebook.com/profile.php?id=61583328372294", label: "Facebook", Icon: FacebookIcon },
                  ].map(({ href, label, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      style={{ color: "rgba(255,255,255,.5)", transition: "color .2s" }}
                      onMouseEnter={e => (e.currentTarget.style.color = "#2EB39A")}
                      onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,.5)")}
                    >
                      <Icon />
                    </a>
                  ))}
                </div>
              </div>
            </nav>
          </div>

          {/* Bottom: copyright | legal */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-[26px]">
            <p style={{ fontSize: 13, color: MUTED }}>
              &copy; {new Date().getFullYear()} Revisr. All rights reserved.
            </p>
            <div className="flex gap-6">
              {legal.map(item => (
                <Link
                  key={item.name}
                  to={item.href}
                  style={{ fontSize: 13, color: MUTED, textDecoration: "none", transition: "color .2s" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#2EB39A")}
                  onMouseLeave={e => (e.currentTarget.style.color = MUTED)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}
